import {
  Injectable,
  Inject,
  Renderer2,
  RendererFactory2,
  Optional,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { SocialLoginComponent } from './social.login.component';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { ScriptLoader } from './script-loader.util';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class CheckLoginStatusService {
  isUserLoggedIn =
    localStorage.getItem('social.login.googleUser') ||
    localStorage.getItem('social.login.facebookUser')
      ? true
      : false;
  profilePicture?: string =
    JSON.parse(
      localStorage.getItem('social.login.googleUser') ||
        localStorage.getItem('social.login.facebookUser') ||
        '{}'
    )?.profilePicture || '';
  provider?: string = localStorage.getItem('social.login.googleUser')
    ? 'google'
    : localStorage.getItem('social.login.facebookUser')
    ? 'facebook'
    : '';

  // Google specific properties
  private isGoogleInitialized = false;
  private googleLibraryLoaded = false;
  private renderer: Renderer2;
  private readonly GOOGLE_SCRIPT_PATH =
    'https://accounts.google.com/gsi/client';
  private readonly GOOGLE_CLIENT_ID =
    '665289826545-463bhl103plveo5l05b94vjnenmr3f7e';

  // Subject to notify when Google is ready
  public googleReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    @Optional() private scriptService?: ScriptService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  checkGoogleLoginStatus(): boolean {
    const stored = localStorage.getItem('social.login.googleUser');
    if (stored) {
      const user = JSON.parse(stored);
      this.isUserLoggedIn = true;
      this.profilePicture = user.profilePicture;
      this.provider = 'google';
      SocialLoginComponent.loginStatus$.next(true);
    }
    return this.isUserLoggedIn;
  }

  checkFacebookLoginStatus(): boolean {
    const stored = localStorage.getItem('social.login.facebookUser');
    if (stored) {
      const user = JSON.parse(stored);
      this.isUserLoggedIn = true;
      this.profilePicture = user.profilePicture;
      this.provider = 'facebook';
      SocialLoginComponent.loginStatus$.next(true);
    }
    return this.isUserLoggedIn;
  }

  // Load Google library
  loadGoogleLibrary(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Skip if in webview
      //if (this.isInWebView()) {
      //this.googleReady$.next(true);
      //resolve();
      //   return;
      // }

      // Skip if already loaded
      if (this.googleLibraryLoaded) {
        resolve();
        return;
      }

      const loadScript = () => {
        // Use ScriptService if available, otherwise use ScriptLoader utility
        if (this.scriptService) {
          if (!this.scriptService.isScriptExist(this.GOOGLE_SCRIPT_PATH)) {
            const scriptElement = this.scriptService.loadJsScript(
              this.renderer,
              this.GOOGLE_SCRIPT_PATH
            );

            scriptElement.onload = () => {
              console.log('Google login script loaded');
              this.googleLibraryLoaded = true;
              this.googleReady$.next(true);
              resolve();
            };

            scriptElement.onerror = () => {
              console.error('Could not load the Google script');
              reject(new Error('Failed to load Google script'));
            };
          } else {
            this.googleLibraryLoaded = true;
            this.googleReady$.next(true);
            resolve();
          }
        } else {
          // Fallback to ScriptLoader utility
          ScriptLoader.loadScript(this.GOOGLE_SCRIPT_PATH)
            .then(() => {
              console.log('Google login script loaded');
              this.googleLibraryLoaded = true;
              this.googleReady$.next(true);
              resolve();
            })
            .catch((error) => {
              console.error('Could not load the Google script');
              reject(error);
            });
        }
      };

      loadScript();
    });
  }

  // Initialize Google Sign-In
  async initializeGoogle(): Promise<void> {
    // Prevent multiple initializations
    if (this.isGoogleInitialized) {
      console.log('Google already initialized');
      this.renderGoogleButtons();
      this.performGoogleInitialization();
      return;
    }

    try {
      // Load library if needed
      if (!this.googleLibraryLoaded) {
        await this.loadGoogleLibrary();
        this.performGoogleInitialization();
      }

      if (this.isInWebView()) {
        // In webview, just check login status and render custom button
        this.checkGoogleLoginStatus();
        //setTimeout(() => this.renderCustomGoogleButton(), 100);
        this.performGoogleInitialization();
      } else {
        // Initialize Google Sign-In
        this.performGoogleInitialization();
      }
    } catch (error) {
      console.error('Error initializing Google:', error);
    }
  }

  private async performGoogleInitialization(): Promise<void> {
    console.log('Initializing Google Sign-In');
    await this.loadGoogleLibrary();

    this.checkGoogleLoginStatus();

    const google = (window as any).google;

    if (google?.accounts?.id?.initialize) {
      google.accounts.id.initialize({
        client_id: this.GOOGLE_CLIENT_ID,
        callback: (response: any) => {
          this.sendLoginRequest('google');
          this.handleGoogleSignIn(response);
        },
      });

      this.isGoogleInitialized = true;
      this.renderGoogleButtons();
    } else {
      console.error('Google Sign-In library not loaded');
    }
  }

  private handleGoogleSignIn(response: any): void {
    console.log('Google sign-in response:', response);

    if (response.credential) {
      const credential: any = jwt_decode(response.credential);
      console.log('Decoded credential:', credential);

      const userData = {
        userId: credential.email + '|google',
        profilePicture: credential.picture,
        email: credential.email,
        userName: credential.name,
      };

      // Update service state
      this.isUserLoggedIn = true;
      this.profilePicture = userData.profilePicture;
      this.provider = 'google';

      // Save to localStorage
      localStorage.setItem('social.login.googleUser', JSON.stringify(userData));

      // Update Google component static properties (if needed for backward compatibility)
      if ((window as any).GoogleLoginComponent) {
        const GoogleLoginComponent = (window as any).GoogleLoginComponent;
        GoogleLoginComponent.isUserLoggedIn = true;
        GoogleLoginComponent.googleUserId = userData.userId;
        GoogleLoginComponent.profilePicture = userData.profilePicture;
        GoogleLoginComponent.email = userData.email;
        GoogleLoginComponent.userName = userData.userName;
      }

      // Notify login status change
      SocialLoginComponent.loginStatus$.next(true);
    }
  }

  // Render Google Sign-In buttons
  renderGoogleButtons(): void {
    const google = (window as any).google;

    if (!google?.accounts?.id?.renderButton) {
      console.warn('Google Sign-In renderButton not available');
      return;
    }

    const buttonContainers =
      this.document.querySelectorAll('.google-signin-btn');
    const popupButtons = this.document.querySelectorAll(
      '.cdk-overlay-container .google-signin-btn'
    );

    const allButtons = Array.from(buttonContainers).concat(
      Array.from(popupButtons)
    );

    allButtons.forEach((element) => {
      if (!element.querySelector('.abcRioButton')) {
        // Check if button already rendered
        google.accounts.id.renderButton(element, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: window.innerWidth >= 400 ? 295 : 260,
        });
      }
    });
  }

  // Render custom button for webview
  private renderCustomGoogleButton(): void {
    const buttonContainers =
      this.document.querySelectorAll('.google-signin-btn');

    buttonContainers.forEach((container: Element) => {
      if (!container.querySelector('.custom-google-btn')) {
        const button = this.document.createElement('button');
        button.className = 'custom-google-btn';
        button.innerHTML = `
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style="margin-right: 8px; vertical-align: middle;">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          <span style="font-family: 'Roboto', arial, sans-serif; font-size: 14px; color: #3c4043; font-weight: 500;">Sign in with Google</span>
        `;
        button.style.cssText = `
          background-color: #fff;
          border: 1px solid #dadce0;
          border-radius: 4px;
          padding: 11px 24px;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          font-family: 'Roboto', arial, sans-serif;
          transition: background-color .218s, border-color .218s;
          width: 100%;
          max-width: 295px;
        `;

        button.onmouseover = () => {
          button.style.backgroundColor = '#f8f9fa';
          button.style.borderColor = '#dadce0';
        };

        button.onmouseout = () => {
          button.style.backgroundColor = '#fff';
          button.style.borderColor = '#dadce0';
        };

        button.onclick = () => {
          this.sendLoginRequest('google');
          this.performGoogleInitialization();
        };

        container.appendChild(button);
      }
    });
  }

  // Send login request via postMessage
  private sendLoginRequest(provider: 'google' | 'facebook'): void {
    const message = {
      type: 'APP_LOGIN_REQUEST',
      provider: provider,
    };

    const host =
      window.parent && window.parent !== window ? window.parent : window;
    console.log('Sending login request:', message);
    host.postMessage(message, '*');
  }

  // Logout methods
  logoutGoogle(): void {
    if (this.isInWebView()) {
      this.sendLogoutRequest('google');
    } else {
      const google = (window as any).google;
      if (google?.accounts?.id?.disableAutoSelect) {
        google.accounts.id.disableAutoSelect();
      }
    }
    this.clearGoogleData();
  }

  logoutFacebook(): void {
    if (this.isInWebView()) {
      this.sendLogoutRequest('facebook');
    } else {
      this.clearFacebookData();
    }
  }

  private sendLogoutRequest(provider: 'google' | 'facebook'): void {
    const message = {
      type: 'APP_LOGOUT_REQUEST',
      provider: provider,
    };

    const host =
      window.parent && window.parent !== window ? window.parent : window;
    console.log('Sending logout request:', message);
    host.postMessage(message, '*');
  }

  private clearGoogleData(): void {
    localStorage.removeItem('social.login.googleUser');
    if (this.provider === 'google') {
      this.isUserLoggedIn = false;
      this.profilePicture = '';
      this.provider = '';
    }
    SocialLoginComponent.loginStatus$.next(false);
  }

  private clearFacebookData(): void {
    localStorage.removeItem('social.login.facebookUser');
    if (this.provider === 'facebook') {
      this.isUserLoggedIn = false;
      this.profilePicture = '';
      this.provider = '';
    }
    SocialLoginComponent.loginStatus$.next(false);
  }

  // Utility methods
  private isInWebView(): boolean {
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Update from app login (for webview integration)
  updateFromAppLogin(loginData: any): void {
    if (loginData.provider === 'google') {
      const userData = {
        userId: loginData.userId,
        profilePicture: loginData.profilePicture,
        email: loginData.email,
        userName: loginData.userName,
      };

      this.isUserLoggedIn = true;
      this.profilePicture = userData.profilePicture;
      this.provider = 'google';

      localStorage.setItem('social.login.googleUser', JSON.stringify(userData));
      SocialLoginComponent.loginStatus$.next(true);
    } else if (loginData.provider === 'facebook') {
      // Handle Facebook login update
      const userData = {
        userId: loginData.userId,
        profilePicture: loginData.profilePicture,
      };

      this.isUserLoggedIn = true;
      this.profilePicture = userData.profilePicture;
      this.provider = 'facebook';

      localStorage.setItem(
        'social.login.facebookUser',
        JSON.stringify(userData)
      );
      SocialLoginComponent.loginStatus$.next(true);
    }
  }
}
